import { gsap } from 'gsap'

export const motionProps = {
	icon: {
		leave: { duration: 0.3, ease: 'power2.in', yPercent: 103 },
		enter: { duration: 0.5, ease: 'power2', yPercent: 0 },
	},
	message: {
		leave: {
			duration: 0.3,
			stagger: { amount: 0.03, ease: 'power1.out' },
			ease: 'power2.in',
			yPercent: -103,
		},
		enter: {
			duration: 0.6,
			stagger: { amount: 0.1, ease: 'power1.out' },
			ease: 'power2',
			yPercent: 0,
		},
	},
}

const memoElements = {
	icon: { active: null, aux: null },
	message: { active: [], aux: [] },
}

export function getBotMessage(variation = 'active') {
	return [...memoElements.message[variation]].filter((element) => element.textContent.trim() !== '')
}

export function getBotIcon(variation = 'active') {
	return memoElements.icon[variation]
}

export function createStateTransition(elements) {
	let { icon, message, container } = elements
	let pending = null
	let running = false
	let slideTimeline = gsap.timeline({ paused: true })

	refresh(elements)

	function refresh(nextElements) {
		icon = nextElements.icon
		message = nextElements.message
		container = nextElements.container

		memoElements.icon.active = icon.active
		memoElements.icon.aux = icon.aux
		memoElements.message.active = message.active
		memoElements.message.aux = message.aux
	}

	function killCurrentAnimation(clean) {
		slideTimeline.kill()
		gsap.killTweensOf([
			icon.active,
			icon.aux,
			...message.active,
			...message.aux,
			...getMessageParents(),
		])
		resetOverflowSlide()

		if (clean) {
			gsap.set([icon.active, icon.aux, ...message.active, ...message.aux], {
				clearProps: 'all',
			})
			pending = null
			running = false
		}
	}

	function request(req) {
		if (!running) {
			run(req)
		} else {
			pending = req
		}
	}

	async function defer(promise) {
		running = true
		resetOverflowSlide()
		await promise
		startOverflowSlide()
		running = false

		if (pending) {
			const next = pending
			pending = null
			run(next)
		}
	}

	async function run(req) {
		running = true
		killCurrentAnimation()

		if (req.immediate) {
			await req.setup()
			await req.complete()
		} else {
			await req.setup()

			const [iconTransitioned, cleanIconTransition] = transitionIcon()
			const [messageTransitioned, cleanMessageTransition] = transitionMessage()

			await Promise.all([iconTransitioned, messageTransitioned])
			await req.complete()

			cleanIconTransition()
			cleanMessageTransition()
			startOverflowSlide()
		}

		running = false

		if (pending) {
			const next = pending
			pending = null
			run(next)
		}
	}

	function transitionIcon() {
		function clean() {
			gsap.set([icon.active, icon.aux], { clearProps: 'all' })
		}

		const promise = new Promise((resolve) => {
			if (icon.active.getAttribute('data-name') === icon.aux.getAttribute('data-name')) {
				resolve()
				return
			}

			gsap.set(icon.active, { yPercent: 0 })
			gsap.set(icon.aux, { yPercent: -110, opacity: 1 })
			gsap.to(icon.active, { ...motionProps.icon.leave, data: { bot: true } })
			gsap.to(icon.aux, {
				...motionProps.icon.enter,
				delay: 0.2,
				onComplete: resolve,
				data: { bot: true },
			})
		})

		return [promise, clean]
	}

	function transitionMessage() {
		function clean() {
			gsap.set([...message.active, ...message.aux], { clearProps: 'all' })
		}

		const inWords = getBotMessage('aux')
		const outWords = getBotMessage('active')

		const promise = new Promise((resolve) => {
			if (
				message.active[0]?.parentElement?.textContent === message.aux[0]?.parentElement?.textContent
			) {
				resolve()
				return
			}

			gsap.set(outWords, { yPercent: 0 })
			gsap.set(inWords, { yPercent: 110, opacity: 1 })
			gsap.to(outWords, { ...motionProps.message.leave })
			gsap.to(inWords, {
				...motionProps.message.enter,
				delay: 0.23,
				onComplete: resolve,
			})
		})

		return [promise, clean]
	}

	function startOverflowSlide() {
		const messageElement = message.active[0]?.parentElement
		const containerElement = container ?? messageElement?.closest('.bot-container')

		resetOverflowSlide()

		if (
			!messageElement ||
			!containerElement ||
			messageElement.scrollWidth <= containerElement.offsetWidth
		) {
			return
		}

		const widthDiff = messageElement.scrollWidth - containerElement.offsetWidth

		slideTimeline = gsap.timeline({
			paused: true,
			repeat: -1,
			yoyo: true,
			repeatDelay: 2,
			delay: 1,
		})

		slideTimeline.to(message.active[0].parentElement, {
			x: -widthDiff - 50,
			ease: 'none',
			duration: () => {
				const speed = 100
				return (Math.abs(widthDiff) + 50) / speed
			},
		})

		slideTimeline.play()
	}

	function resetOverflowSlide() {
		slideTimeline.kill()
		gsap.set(getMessageParents(), { clearProps: 'x,transform' })
	}

	function getMessageParents() {
		return [message.active[0]?.parentElement, message.aux[0]?.parentElement].filter(Boolean)
	}

	return {
		defer,
		request,
		refresh,
		killCurrentAnimation,
	}
}
